import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Plane, Lock } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate("/");
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) navigate("/");
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
        extraParams: {
          prompt: "select_account",
          login_hint: "darelegion5@gmail.com",
        },
      });

      if (result.error) {
        throw new Error(result.error.message || "Sign-in failed");
      }
      // result.redirected → browser will navigate to Google
    } catch (error: any) {
      toast({
        title: "Sign-in failed",
        description: error.message || "Please choose the approved Google account for admin access.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign in – Sky Lofi ATC</title>
        <meta name="description" content="Admin sign-in for Sky Lofi ATC." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://skylofiatc.com/auth" />
        <meta property="og:title" content="Sign in – Sky Lofi ATC" />
        <meta property="og:description" content="Admin sign-in for Sky Lofi ATC." />
        <meta property="og:url" content="https://skylofiatc.com/auth" />
      </Helmet>
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="glass-card p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Plane className="h-12 w-12 mb-4 text-primary" />
          <h1 className="text-3xl font-bold mb-2">Sky Lofi ATC</h1>
          <p className="text-muted-foreground text-center text-sm flex items-center gap-2">
            <Lock className="h-3.5 w-3.5" />
            Admin access only
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {loading ? "Redirecting..." : "Sign in with Google"}
        </Button>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          This site is locked down. Only pre-approved accounts can sign in.
        </p>
      </Card>
    </div>
    </>
  );
}
